import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { BarChart3, TrendingUp, Users, Calendar, ArrowUpRight } from 'lucide-react';
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }
};

const COLORS = ['#22c55e', '#6366f1', '#a855f7', '#ef4444', '#f59e0b'];

const tooltipStyle = {
  backgroundColor: '#1c1c1f',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '12px',
  color: '#ececef',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  padding: '12px 16px'
};

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [statsRes, revenueRes, clientsRes, servicesRes, aptsRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/stats`, { headers }),
        axios.get(`${API}/api/dashboard/revenue-chart`, { headers }),
        axios.get(`${API}/api/clients`, { headers }),
        axios.get(`${API}/api/services`, { headers }),
        axios.get(`${API}/api/appointments`, { headers }),
      ]);
      setStats(statsRes.data);
      setRevenueData(revenueRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      setAppointments(aptsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getServicePopularity = () => {
    const serviceCount = {};
    appointments.forEach(apt => {
      serviceCount[apt.service_id] = (serviceCount[apt.service_id] || 0) + 1;
    });
    return services.map(s => ({
      name: s.name,
      value: serviceCount[s.id] || 0,
      color: s.color
    })).filter(s => s.value > 0);
  };

  const getClientGrowth = () => {
    const months = {};
    clients.forEach(client => {
      if (client.created_at) {
        const date = new Date(client.created_at);
        const key = date.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
        months[key] = (months[key] || 0) + 1;
      }
    });
    return Object.entries(months).slice(-6).map(([month, count]) => ({ month, clients: count }));
  };

  const getMembershipStatus = () => {
    const now = new Date();
    let active = 0, expiring = 0, expired = 0;
    
    clients.forEach(client => {
      if (client.membership_end) {
        const endDate = new Date(client.membership_end);
        const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        if (daysRemaining < 0) expired++;
        else if (daysRemaining <= 7) expiring++;
        else active++;
      }
    });

    return [
      { name: 'Activos', value: active, color: '#22c55e' },
      { name: 'Por vencer', value: expiring, color: '#f59e0b' },
      { name: 'Vencidos', value: expired, color: '#ef4444' },
    ].filter(s => s.value > 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-8">
          <div className="h-10 w-48 nova-skeleton" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-28 nova-skeleton rounded-xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-80 nova-skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const servicePopularity = getServicePopularity();
  const clientGrowth = getClientGrowth();
  const membershipStatus = getMembershipStatus();

  const statCards = [
    { title: 'Total Clientes', value: stats?.total_clients || 0, icon: Users, color: 'indigo' },
    { title: 'Activos', value: stats?.active_memberships || 0, icon: TrendingUp, color: 'emerald' },
    { title: 'Citas Hoy', value: stats?.todays_appointments || 0, icon: Calendar, color: 'amber' },
    { title: 'Ingresos Mes', value: formatCurrency(stats?.monthly_revenue || 0), icon: BarChart3, color: 'emerald', isCurrency: true },
  ];

  const colorMap = {
    indigo: { bg: 'from-indigo-500/12 to-indigo-600/5', icon: 'bg-indigo-500/15 text-indigo-400' },
    emerald: { bg: 'from-emerald-500/12 to-emerald-600/5', icon: 'bg-emerald-500/15 text-emerald-400' },
    amber: { bg: 'from-amber-500/12 to-amber-600/5', icon: 'bg-amber-500/15 text-amber-400' },
  };

  return (
    <Layout>
      <motion.div 
        data-testid="reports-page" 
        className="space-y-8"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item}>
          <h1 className="text-headline text-white">Reportes</h1>
          <p className="text-caption mt-1">Análisis y métricas de tu negocio</p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div variants={container} className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {statCards.map((stat, i) => {
            const style = colorMap[stat.color];
            return (
              <motion.div
                key={i}
                variants={item}
                className={`nova-card p-5 bg-gradient-to-br ${style.bg}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${style.icon}`}>
                    <stat.icon className="w-4 h-4" />
                  </div>
                  <span className="text-caption">{stat.title}</span>
                </div>
                <p className="nova-stat-value text-2xl">{stat.value}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <motion.div variants={item} className="nova-card p-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-title text-white">Ingresos Mensuales</h3>
                <p className="text-caption mt-0.5">Últimos 6 meses</p>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-semibold">
                <ArrowUpRight className="w-3.5 h-3.5" />
                Tendencia
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="reportRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#6e6e73', fontSize: 12, fontWeight: 500 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#6e6e73', fontSize: 12 }}
                    tickFormatter={(value) => `$${value/1000}k`}
                    dx={-10}
                  />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    formatter={(value) => [formatCurrency(value), 'Ingresos']}
                    labelStyle={{ color: '#6e6e73', marginBottom: '4px' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#6366f1" 
                    strokeWidth={2.5}
                    fillOpacity={1} 
                    fill="url(#reportRevenueGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Membership Status */}
          <motion.div variants={item} className="nova-card p-6">
            <div className="mb-8">
              <h3 className="text-title text-white">Estado de Membresías</h3>
              <p className="text-caption mt-0.5">Distribución actual</p>
            </div>
            <div className="h-64 flex items-center justify-center">
              {membershipStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={membershipStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {membershipStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span style={{ color: '#a1a1a6', fontSize: '13px' }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[var(--nova-surface-raised)] flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="w-7 h-7 text-[var(--nova-text-quaternary)]" />
                  </div>
                  <p className="text-caption">Sin datos disponibles</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Client Growth */}
          <motion.div variants={item} className="nova-card p-6">
            <div className="mb-8">
              <h3 className="text-title text-white">Crecimiento de Clientes</h3>
              <p className="text-caption mt-0.5">Nuevos registros por mes</p>
            </div>
            <div className="h-64">
              {clientGrowth.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientGrowth}>
                    <XAxis 
                      dataKey="month" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6e6e73', fontSize: 12, fontWeight: 500 }}
                      dy={10}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6e6e73', fontSize: 12 }}
                      dx={-10}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="clients" fill="#6366f1" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-caption">Sin datos disponibles</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Service Popularity */}
          <motion.div variants={item} className="nova-card p-6">
            <div className="mb-8">
              <h3 className="text-title text-white">Servicios Populares</h3>
              <p className="text-caption mt-0.5">Citas por servicio</p>
            </div>
            <div className="h-64">
              {servicePopularity.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={servicePopularity} layout="vertical">
                    <XAxis 
                      type="number" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6e6e73', fontSize: 12 }}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#a1a1a6', fontSize: 12, fontWeight: 500 }}
                      width={100}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                      {servicePopularity.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-caption">Sin citas registradas</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
}
